'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import ChannelBadge from '@/components/ui/ChannelBadge';
import type { CallState } from './ActiveInteractionContent';
import { toast } from 'sonner';
import { getMIRRef } from '@/lib/demoDate';

const MIR_REF = getMIRRef(14892);

interface Props {
  callState: CallState;
  duration: string;
  onEndCall: () => void;
  showTransfer?: boolean;
  onToggleTransfer?: () => void;
  onConsult?: () => void;
}

export default function CallColumn({ callState, duration, onEndCall, showTransfer, onToggleTransfer, onConsult }: Props) {
  const [muted, setMuted] = useState(false);
  const [onHold, setOnHold] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  const getCallStateLabel = () => {
    switch (callState) {
      case 'active': return onHold
        ? { label: 'ON HOLD', cls: 'bg-amber-500/20 text-amber-300' }
        : { label: 'LIVE', cls: 'bg-red-500/20 text-red-300' };
      case 'ended': return { label: 'CALL ENDED', cls: 'bg-white/10 text-white/60' };
      case 'ai-processing': return { label: 'AI PROCESSING', cls: 'bg-blue-500/20 text-blue-300' };
      case 'ai-draft-ready': return { label: 'DRAFT READY', cls: 'bg-purple-500/20 text-purple-300' };
      case 'post-call': return { label: 'POST-CALL', cls: 'bg-blue-500/20 text-blue-300' };
    }
  };

  const stateInfo = getCallStateLabel();

  const handleEndCallClick = () => {
    setShowEndConfirm(true);
  };

  const handleConfirmEnd = () => {
    setShowEndConfirm(false);
    onEndCall();
  };

  return (
    <>
      {/* End Call Confirmation Modal */}
      {showEndConfirm && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-2xl shadow-modal p-5 w-[380px] slide-in-up mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <Icon name="PhoneXMarkIcon" size={18} className="text-red-600" />
              </div>
              <div>
                <h2 className="text-[15px] font-700 text-foreground">End this call?</h2>
                <p className="text-[11px] text-muted-foreground">Ahmed Firdaus · MyGCC General</p>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              {[
                { label: 'Call Duration', value: duration },
                { label: 'MIR Recording', value: MIR_REF },
              ].map(row => (
                <div key={row.label} className="flex items-center justify-between text-[12px] bg-muted/40 rounded-xl px-3 py-2">
                  <span className="text-muted-foreground">{row.label}</span>
                  <span className="font-600 font-mono text-[11px]">{row.value}</span>
                </div>
              ))}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-[11px] text-blue-700">
              Recording will be finalised by Mitel MIR. CX AI Assist will prepare the interaction summary.
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowEndConfirm(false)} className="flex-1 btn-secondary py-2.5 text-[13px]">Cancel</button>
              <button onClick={handleConfirmEnd} className="flex-1 py-2.5 text-[13px] bg-red-600 hover:bg-red-700 text-white rounded-xl font-700 transition-colors flex items-center justify-center gap-2">
                <Icon name="PhoneXMarkIcon" size={15} />
                End Call
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* Active Interaction Card */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Icon name="PhoneIcon" size={13} className="text-primary" />
            <span className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Active Interaction</span>
          </div>
          <div className="bg-nav-bg rounded-xl p-3 text-white">
            <div className="flex items-center gap-2 mb-2">
              <ChannelBadge channel="PHONE" />
              <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-600 ${stateInfo.cls}`}>
                {callState === 'active' && !onHold && <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse inline-block" />}
                {callState === 'active' && onHold && <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse inline-block" />}
                {callState === 'ai-processing' && <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse inline-block" />}
                {stateInfo.label}
              </span>
              {muted && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-600 bg-red-500/20 text-red-300 flex items-center gap-1">
                  <Icon name="MicrophoneIcon" size={10} />
                  MUTED
                </span>
              )}
            </div>
            <div className="text-[16px] font-700 mb-0.5">Ahmed Firdaus</div>
            <div className="text-[12px] text-white/60 mb-2">+6012 *** 4581</div>
            <div className="text-[28px] font-700 font-tabular text-white mb-2">{duration}</div>
            <div className="grid grid-cols-2 gap-1.5 text-[11px]">
              <div className="bg-white/10 rounded-lg px-2 py-1">
                <div className="text-white/50">Queue</div>
                <div className="font-600 text-white/90">MyGCC General</div>
              </div>
              <div className="bg-white/10 rounded-lg px-2 py-1">
                <div className="text-white/50">Language</div>
                <div className="font-600 text-white/90">Bahasa Melayu</div>
              </div>
              <div className="bg-white/10 rounded-lg px-2 py-1">
                <div className="text-white/50">Agency</div>
                <div className="font-600 text-white/90">KKM</div>
              </div>
              <div className="bg-white/10 rounded-lg px-2 py-1">
                <div className="text-white/50">IC</div>
                <div className="font-600 text-white/90">****-**-4821</div>
              </div>
            </div>
            {onHold && (
              <div className="mt-2 bg-amber-500/20 border border-amber-400/30 rounded-lg px-2 py-1.5 text-[11px] text-amber-200 text-center font-600">
                ⏸ Call on Hold — Citizen is waiting
              </div>
            )}
          </div>
        </div>

        {/* Call Controls */}
        {callState === 'active' && (
          <div>
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Call Controls</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                {
                  label: onHold ? 'Resume' : 'Hold',
                  icon: onHold ? 'PlayIcon' : 'PauseIcon',
                  active: onHold,
                  action: () => {
                    setOnHold(v => !v);
                    toast.info(onHold ? 'Call resumed' : 'Call on hold', {
                      description: onHold ? 'Ahmed Firdaus reconnected' : 'Ahmed Firdaus placed on hold',
                    });
                  },
                },
                {
                  label: muted ? 'Unmute' : 'Mute',
                  icon: muted ? 'MicrophoneIcon' : 'MicrophoneIcon',
                  active: muted,
                  action: () => {
                    setMuted(v => !v);
                    toast.info(muted ? 'Microphone unmuted' : 'Microphone muted');
                  },
                },
                {
                  label: 'Transfer',
                  icon: 'ArrowRightOnRectangleIcon',
                  active: showTransfer,
                  action: onToggleTransfer || (() => {}),
                },
                {
                  label: 'Consult',
                  icon: 'UserPlusIcon',
                  active: false,
                  action: onConsult || (() => toast.info('Consult — connecting supervisor')),
                },
                {
                  label: 'Record',
                  icon: 'CircleStackIcon',
                  active: true,
                  action: () => toast.info('Recording active via Mitel MIR — ' + MIR_REF),
                  disabled: true,
                },
                {
                  label: 'Keypad',
                  icon: 'CalculatorIcon',
                  active: showKeypad,
                  action: () => setShowKeypad(v => !v),
                },
              ].map((ctrl) => (
                <button
                  key={`ctrl-${ctrl.label}`}
                  onClick={ctrl.action}
                  title={ctrl.label}
                  disabled={ctrl.disabled}
                  className={`flex flex-col items-center gap-1 py-2 rounded-lg text-[11px] font-500 transition-all duration-150 active:scale-95 border
                    ${ctrl.active
                      ? 'bg-primary text-white border-primary' :'bg-white text-muted-foreground border-border hover:bg-secondary hover:text-primary hover:border-primary'
                    }
                    ${ctrl.disabled ? 'opacity-70 cursor-default' : ''}
                  `}
                >
                  <Icon name={ctrl.icon as Parameters<typeof Icon>[0]['name']} size={15} />
                  {ctrl.label}
                </button>
              ))}
            </div>

            {/* Keypad */}
            {showKeypad && (
              <div className="mt-2 p-3 bg-muted/50 rounded-xl border border-border">
                <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2 text-center">DTMF Keypad</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {['1','2','3','4','5','6','7','8','9','*','0','#'].map(k => (
                    <button
                      key={k}
                      onClick={() => toast.info(`Pressed ${k}`)}
                      className="py-2.5 text-[15px] font-700 bg-white border border-border rounded-xl hover:bg-secondary hover:border-primary transition-colors active:scale-95"
                    >
                      {k}
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowKeypad(false)} className="w-full mt-2 btn-ghost py-1.5 text-[11px] border border-border">
                  Close Keypad
                </button>
              </div>
            )}

            <button
              onClick={handleEndCallClick}
              className="w-full mt-3 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl font-700 text-[13px] flex items-center justify-center gap-2 transition-colors active:scale-98"
            >
              <Icon name="PhoneXMarkIcon" size={16} />
              End Call
            </button>
          </div>
        )}

        {/* AI Processing state */}
        {callState === 'ai-processing' && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
            <div className="flex justify-center gap-1 mb-2">
              {[0,1,2].map(i => <span key={i} className="w-2 h-2 bg-blue-500 rounded-full ai-listening-dot" style={{ animationDelay: `${i * 0.16}s` }} />)}
            </div>
            <div className="text-[13px] font-600 text-blue-700">AI Processing</div>
            <div className="text-[11px] text-blue-600 mt-1">CX AI Assist is analysing the interaction recording</div>
            <div className="text-[10px] text-muted-foreground mt-1.5">You can handle other interactions while AI processes</div>
          </div>
        )}

        {/* AI Draft Ready state */}
        {callState === 'ai-draft-ready' && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
            <div className="text-[16px] mb-1">✨</div>
            <div className="text-[13px] font-600 text-purple-700">AI Draft Ready</div>
            <div className="text-[11px] text-purple-600 mt-1">Interaction summary prepared for Ahmed Firdaus</div>
            <div className="text-[10px] text-muted-foreground mt-1.5">Review the AI Assisted Wrap-Up in the right panel</div>
          </div>
        )}

        {callState === 'ended' && (
          <div className="bg-slate-50 border border-border rounded-xl p-3 text-center">
            <Icon name="PhoneXMarkIcon" size={20} className="text-slate-400 mx-auto mb-1.5" />
            <div className="text-[13px] font-600 text-slate-600">Call Ended</div>
            <div className="text-[12px] text-muted-foreground">Duration: {duration}</div>
          </div>
        )}

        {/* Contact Information */}
        <div>
          <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Contact Information</div>
          <div className="space-y-2 text-[12px]">
            {[
              { label: 'Phone', value: '+6012 *** 4581', icon: 'PhoneIcon' },
              { label: 'Email', value: 'a.firdaus@***.my', icon: 'EnvelopeIcon' },
              { label: 'Language', value: 'Bahasa Melayu', icon: 'LanguageIcon' },
              { label: 'Location', value: 'Selangor', icon: 'MapPinIcon' },
              { label: 'IC', value: '****-**-4821', icon: 'IdentificationIcon' },
            ].map((field) => (
              <div key={`contact-${field.label}`} className="flex items-center gap-2">
                <Icon name={field.icon as Parameters<typeof Icon>[0]['name']} size={13} className="text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">{field.label}:</span>
                <span className="font-500 text-foreground">{field.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Mitel MIR */}
        <div className="border border-border rounded-xl p-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Mitel MIR Recording</div>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-600 ${
              callState === 'active' ? 'bg-red-50 text-red-600' :
              callState === 'ai-processing'? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'
            }`}>
              {callState === 'active' ? 'Recording' : callState === 'ai-processing' ? 'Finalising' : 'Finalised'}
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground mb-1 font-mono">{MIR_REF}</div>
          <div className="flex items-center gap-1 mt-2">
            <button onClick={() => toast.info('Playing recording')} className="p-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
              <Icon name="PlayIcon" size={11} />
            </button>
            <button onClick={() => toast.info('Paused')} className="p-1.5 bg-muted text-muted-foreground rounded-lg hover:bg-border transition-colors">
              <Icon name="PauseIcon" size={11} />
            </button>
            <div className="flex-1 mx-1.5 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full" style={{ width: callState === 'active' ? '40%' : '100%' }} />
            </div>
            <span className="text-[10px] font-tabular text-muted-foreground">{callState === 'active' ? duration : '06:43'}</span>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1.5">Voice recording managed by Mitel Communication Foundation</div>
        </div>
      </div>
    </>
  );
}